/**
 * Auth + onboarding redirect gate.
 *
 * Sprint S13.1.C (Day 9) — simplified к single canonical onboarding path
 * `/onboarding` (Phase E backend, Zustand wizard). Legacy `/setup` route
 * still resolves at App.tsx level via `<Navigate to="/onboarding">` — by the
 * time RequireAuth runs, location.pathname is already `/onboarding`.
 *
 * Redirect rules:
 *   1. No token → /login
 *   2. Loading /api/me → render nothing (avoid flash of gated content)
 *   3. Un-onboarded user NOT on /onboarding → redirect to /onboarding
 *   4. Onboarded users may visit /onboarding intentionally — OnboardingPage's
 *      hydration handles consent state and either lets them edit OR redirects
 *      к / when `informed_consent_signed=true`. RequireAuth does NOT bounce.
 *
 * /api/me errors (network / 401) fall through к render children — the axios
 * interceptor handles 401 redirects separately.
 */
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { api } from "@/api/client";

interface UserMeResponse {
  id: number;
  email: string;
  onboarding_completed_at: string | null;
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const location = useLocation();
  const meQ = useQuery({
    queryKey: ["auth-me"],
    queryFn: async () => {
      const { data } = await api.get<UserMeResponse>("/auth/me");
      return data;
    },
    enabled: !!token,
    staleTime: 5 * 60_000,
  });

  if (!token) return <Navigate to="/login" replace />;
  if (meQ.isLoading) return null;
  if (meQ.isError || !meQ.data) return <>{children}</>;

  const isOnboarded = meQ.data.onboarding_completed_at !== null;
  const onOnboarding = location.pathname.startsWith("/onboarding");

  if (!isOnboarded && !onOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}
